import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth, useLanguage, useTheme } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  Sun,
  Moon,
  Globe,
  Sparkles,
  Stethoscope,
  Activity,
  Scissors,
  Sparkle,
  ShieldCheck,
  Brain,
  Cloud,
  Zap,
  LineChart,
  Lock,
  Cpu,
  Layers,
  Mail,
  Play,
  Quote,
  Star,
  BadgeCheck,
  Smartphone,
  Rocket,
} from "lucide-react";

/* ============================================================
   SECTOR DEFINITIONS — drive the routing hub
============================================================ */
const SECTORS = [
  {
    key: "dentisti",
    route: "/dentista",
    image: "/sectors/dental.png",
    icon: Stethoscope,
    accent: "#3B82F6",        // electric blue
    glowFrom: "rgba(59,130,246,0.55)",
    glowTo: "rgba(14,165,233,0.0)",
    it: {
      eyebrow: "Settore",
      title: "Studi Dentistici",
      lede:
        "Gestione clinica completa, anamnesi guidata da AI, ricette SSN e flussi PRO certificati.",
      cta: "Esplora SmartBooker Dental",
    },
    en: {
      eyebrow: "Sector",
      title: "Dental Practices",
      lede:
        "Complete clinical management, AI-guided medical history, SSN prescriptions and certified PRO workflows.",
      cta: "Explore SmartBooker Dental",
    },
  },
  {
    key: "wellness",
    route: "/benessere-posturale",
    image: "/sectors/wellness.png",
    icon: Activity,
    accent: "#10B981",        // emerald
    glowFrom: "rgba(16,185,129,0.55)",
    glowTo: "rgba(20,184,166,0.0)",
    it: {
      eyebrow: "Settore",
      title: "Benessere & Postura",
      lede:
        "Fisioterapia, osteopatia e posturologia con cartella clinica intelligente e routine personalizzate.",
      cta: "Esplora SmartBooker Wellness",
    },
    en: {
      eyebrow: "Sector",
      title: "Wellness & Posture",
      lede:
        "Physiotherapy, osteopathy and posturology with smart clinical records and personalized routines.",
      cta: "Explore SmartBooker Wellness",
    },
  },
  {
    key: "hair",
    route: "/hair",
    image: "/sectors/hair.png",
    icon: Scissors,
    accent: "#F59E0B",        // amber-gold
    glowFrom: "rgba(245,158,11,0.55)",
    glowTo: "rgba(244,114,182,0.0)",
    it: {
      eyebrow: "Settore",
      title: "Hair & Barber",
      lede:
        "Booking online, fidelizzazione e analytics studiati per saloni di parrucchieri e barber shop premium.",
      cta: "Esplora SmartBooker Hair",
    },
    en: {
      eyebrow: "Sector",
      title: "Hair & Barber",
      lede:
        "Online booking, loyalty and analytics crafted for premium hair salons and barber shops.",
      cta: "Explore SmartBooker Hair",
    },
  },
  {
    key: "beauty",
    route: "/estetica",
    image: "/sectors/beauty.png",
    icon: Sparkle,
    accent: "#EC4899",        // rose
    glowFrom: "rgba(236,72,153,0.55)",
    glowTo: "rgba(168,85,247,0.0)",
    it: {
      eyebrow: "Settore",
      title: "Estetica & Beauty",
      lede:
        "Schede cliente avanzate, cabine, percorsi trattamento e CRM per centri estetici e medical spa.",
      cta: "Esplora SmartBooker Beauty",
    },
    en: {
      eyebrow: "Sector",
      title: "Aesthetics & Beauty",
      lede:
        "Advanced client records, cabins, treatment journeys and CRM for beauty centers and medical spas.",
      cta: "Explore SmartBooker Beauty",
    },
  },
];

/* ============================================================
   TOP NAV — minimal enterprise
============================================================ */
const TopNav = ({ language, changeLanguage, theme, toggleTheme }) => {
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="landing-top-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/70 dark:bg-slate-950/70 border-b border-slate-200/70 dark:border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
          <img
            src="/brand/sb-icon.png"
            alt="SmartBooker"
            className="h-8 w-8 drop-shadow-[0_0_14px_rgba(56,189,248,0.35)]"
          />
          <span className="font-semibold tracking-tight text-slate-900 dark:text-white">
            SmartBooker
          </span>
          <span className="hidden sm:inline-block ml-2 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider bg-slate-900 text-white dark:bg-white dark:text-slate-900">
            Platform
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          <a
            href="#sectors"
            className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
            data-testid="nav-sectors"
          >
            {language === "it" ? "Settori" : "Sectors"}
          </a>
          <a
            href="#platform"
            className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
            data-testid="nav-platform"
          >
            {language === "it" ? "Piattaforma" : "Platform"}
          </a>
          <a
            href="#tech"
            className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
            data-testid="nav-tech"
          >
            {language === "it" ? "Perché SmartBooker" : "Why SmartBooker"}
          </a>
          <a
            href="#demo"
            className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
            data-testid="nav-demo"
          >
            Demo
          </a>
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => changeLanguage(language === "it" ? "en" : "it")}
            className="hidden sm:flex h-9 px-3 items-center gap-1.5 rounded-full text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
            data-testid="lang-toggle"
          >
            <Globe className="w-3.5 h-3.5" />
            {language.toUpperCase()}
          </button>
          <button
            onClick={toggleTheme}
            className="h-9 w-9 inline-flex items-center justify-center rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
            data-testid="theme-toggle"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          {user ? (
            <Link to="/dashboard">
              <Button
                size="sm"
                className="rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                data-testid="dashboard-btn"
              >
                Dashboard
              </Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button
                size="sm"
                variant="ghost"
                className="rounded-full text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5"
                data-testid="login-btn"
              >
                {language === "it" ? "Accedi" : "Sign in"}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

/* ============================================================
   HERO — institutional, no pricing, no trial
============================================================ */
const Hero = ({ language }) => {
  const scrollToSectors = () => {
    const el = document.getElementById("sectors");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      className="relative pt-28 sm:pt-32 lg:pt-40 pb-20 sm:pb-28 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Grid backdrop */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white to-slate-50 dark:from-[#070B14] dark:via-[#070B14] dark:to-[#050810]" />
        <div
          className="absolute inset-0 opacity-[0.15] dark:opacity-[0.18]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(15,23,42,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.08) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage:
              "radial-gradient(ellipse 70% 50% at 50% 0%, black 40%, transparent 80%)",
          }}
        />
        {/* Aurora glow */}
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1100px] h-[600px] rounded-full blur-[120px] opacity-40 dark:opacity-50"
             style={{ background: "radial-gradient(closest-side, rgba(56,189,248,0.55), rgba(99,102,241,0.25), transparent)" }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 dark:text-white leading-[1.05]"
              data-testid="hero-headline"
            >
              {language === "it" ? (
                <>
                  Un solo OS verticale
                  <br />
                  per la tua attività.
                  <br />
                  <span className="bg-gradient-to-r from-cyan-400 via-sky-400 to-indigo-400 bg-clip-text text-transparent">
                    Quattro mondi, una piattaforma.
                  </span>
                </>
              ) : (
                <>
                  One vertical OS
                  <br />
                  for your business.
                  <br />
                  <span className="bg-gradient-to-r from-cyan-400 via-sky-400 to-indigo-400 bg-clip-text text-transparent">
                    Four worlds, one platform.
                  </span>
                </>
              )}
            </h1>

            <p
              className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-600 dark:text-slate-300"
              data-testid="hero-subhead"
            >
              {language === "it"
                ? "SmartBooker è la piattaforma enterprise progettata per studi dentistici, centri di benessere posturale, saloni hair e centri estetici. Operations, cartelle cliniche, AI e compliance — in un unico sistema."
                : "SmartBooker is the enterprise platform built for dental clinics, posture wellness centers, hair salons and beauty centers. Operations, clinical records, AI and compliance — in one system."}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <button
                onClick={scrollToSectors}
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-medium text-sm tracking-wide hover:bg-slate-800 dark:hover:bg-slate-100 transition-all hover:translate-y-[-1px] shadow-lg shadow-slate-900/10 dark:shadow-white/10"
                data-testid="hero-cta-sectors"
              >
                {language === "it" ? "Scegli il tuo settore" : "Choose your sector"}
                <ChevronDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
              </button>
              <Link
                to="/demo"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-slate-900/15 dark:border-white/15 text-slate-900 dark:text-white font-medium text-sm tracking-wide hover:bg-slate-900/[0.04] dark:hover:bg-white/5 transition-all"
                data-testid="hero-cta-demo"
              >
                <Play className="w-4 h-4" />
                {language === "it" ? "Guarda la demo" : "Watch the demo"}
              </Link>
            </div>

            {/* Trust line */}
            <div
              className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400"
              data-testid="hero-trust"
            >
              <span>{language === "it" ? "Conforme GDPR" : "GDPR-compliant"}</span>
              <span className="w-px h-3 bg-slate-300 dark:bg-white/15" />
              <span>{language === "it" ? "Hosting UE" : "EU Hosting"}</span>
              <span className="w-px h-3 bg-slate-300 dark:bg-white/15" />
              <span>{language === "it" ? "AI Claude · Gemini" : "AI Claude · Gemini"}</span>
              <span className="w-px h-3 bg-slate-300 dark:bg-white/15" />
              <span>SSO Ready</span>
            </div>
          </div>

          {/* Right visual — stacked sector composition */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-square max-w-md mx-auto">
              <div
                className="absolute inset-0 rounded-[2.5rem] overflow-hidden border border-slate-900/10 dark:border-white/10 shadow-2xl shadow-slate-900/20 dark:shadow-black/60 rotate-[3deg] hover:rotate-0 transition-transform duration-700"
                data-testid="hero-visual-dental"
              >
                <img src="/sectors/dental.png" alt="Dental" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/40 via-transparent to-transparent" />
              </div>
              <div
                className="absolute top-8 -right-4 w-44 aspect-[4/5] rounded-[1.75rem] overflow-hidden border border-slate-900/10 dark:border-white/10 shadow-2xl rotate-[6deg] hover:rotate-[2deg] transition-transform duration-700"
                data-testid="hero-visual-beauty"
              >
                <img src="/sectors/beauty.png" alt="Beauty" className="w-full h-full object-cover" />
              </div>
              <div
                className="absolute bottom-6 -left-4 w-44 aspect-[4/5] rounded-[1.75rem] overflow-hidden border border-slate-900/10 dark:border-white/10 shadow-2xl -rotate-[8deg] hover:-rotate-[3deg] transition-transform duration-700"
                data-testid="hero-visual-hair"
              >
                <img src="/sectors/hair.png" alt="Hair" className="w-full h-full object-cover" />
              </div>
              {/* Floating live tag */}
              <div className="absolute -bottom-4 right-4 px-3 py-2 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 shadow-xl flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                <span className="text-[11px] font-medium text-slate-700 dark:text-slate-200">
                  {language === "it" ? "Live in 4 verticali" : "Live across 4 verticals"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   SECTOR HUB — the 4 vertical cards
============================================================ */
const SectorCard = ({ sector, language, index }) => {
  const t = sector[language] || sector.it;
  const Icon = sector.icon;
  return (
    <Link
      to={sector.route}
      className="group relative block rounded-3xl overflow-hidden bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 transition-all duration-500 hover:-translate-y-2 hover:border-slate-300 dark:hover:border-white/20"
      style={{ animationDelay: `${index * 80}ms` }}
      data-testid={`sector-card-${sector.key}`}
    >
      {/* Image area with overlay */}
      <div className="relative aspect-[4/5] overflow-hidden">
        <img
          src={sector.image}
          alt={t.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
        />
        {/* Vignette */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
        {/* Accent glow on hover */}
        <div
          className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-3/4 h-32 rounded-full blur-3xl opacity-0 group-hover:opacity-90 transition-opacity duration-500"
          style={{ background: sector.glowFrom }}
        />

        {/* Content overlay */}
        <div className="absolute inset-x-0 bottom-0 p-6 text-white">
          <h3 className="text-2xl font-semibold tracking-tight" data-testid={`sector-title-${sector.key}`}>
            {t.title}
          </h3>
          <p className="mt-2 text-sm text-white/75 leading-relaxed line-clamp-3 group-hover:text-white transition-colors">
            {t.lede}
          </p>

          <div
            className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-white"
            data-testid={`sector-cta-${sector.key}`}
          >
            <span className="relative">
              {t.cta}
              <span
                className="absolute -bottom-0.5 left-0 h-px w-0 group-hover:w-full transition-all duration-500"
                style={{ background: sector.accent }}
              />
            </span>
            <ArrowUpRight
              className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45"
              style={{ color: sector.accent }}
            />
          </div>
        </div>
      </div>
    </Link>
  );
};

const SectorHub = ({ language }) => {
  return (
    <section
      id="sectors"
      className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-[#050810]"
      data-testid="sector-hub"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 dark:text-white"
              data-testid="sector-hub-title"
            >
              {language === "it" ? (
                <>
                  Scegli il tuo settore.
                  <br />
                  <span className="text-slate-500 dark:text-slate-400">
                    Scopri la tua piattaforma.
                  </span>
                </>
              ) : (
                <>
                  Choose your sector.
                  <br />
                  <span className="text-slate-500 dark:text-slate-400">
                    Discover your platform.
                  </span>
                </>
              )}
            </h2>
          </div>
          <p className="max-w-md text-base text-slate-600 dark:text-slate-400">
            {language === "it"
              ? "Ogni verticale è una soluzione completa con workflow, cartelle cliniche e moduli AI progettati per il tuo settore — senza compromessi."
              : "Each vertical is a complete solution with workflows, clinical records and AI modules designed for your sector — no compromises."}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {SECTORS.map((s, i) => (
            <SectorCard key={s.key} sector={s} language={language} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   COMPARATIVE — "One platform vs many tools"
============================================================ */
const Comparative = ({ language }) => {
  const COPY = {
    it: {
      title: "Un'unica piattaforma. Zero strumenti dispersi.",
      sub: "Smetti di assemblare gestionali, fogli excel e tool isolati. SmartBooker unifica tutto in un sistema progettato per il tuo verticale.",
      legacy: "Approccio tradizionale",
      modern: "Approccio SmartBooker",
      legacyItems: [
        "Gestionale generico non specifico per settore",
        "Cartelle cliniche separate da appuntamenti",
        "Excel e WhatsApp per recall e follow-up",
        "Niente AI · niente automazioni",
        "Compliance gestita a mano",
        "Dati frammentati tra 5 strumenti diversi",
      ],
      modernItems: [
        "Verticale dedicato a Dental, Wellness, Hair, Beauty",
        "Cartella clinica integrata nativa con calendario",
        "Recall automatici, lista attesa intelligente, WhatsApp",
        "AI per anamnesi, intake, ricette, suggerimenti clinici",
        "GDPR · consensi · firme digitali by-design",
        "Unico data layer · analytics in tempo reale",
      ],
    },
    en: {
      title: "One platform. Zero scattered tools.",
      sub: "Stop stitching together generic managers, spreadsheets and isolated tools. SmartBooker unifies everything in one system built for your vertical.",
      legacy: "Legacy approach",
      modern: "SmartBooker approach",
      legacyItems: [
        "Generic management software, not sector-specific",
        "Clinical records disconnected from appointments",
        "Excel and WhatsApp for recall and follow-up",
        "No AI · no automation",
        "Manual compliance management",
        "Data fragmented across 5 different tools",
      ],
      modernItems: [
        "Dedicated vertical for Dental, Wellness, Hair, Beauty",
        "Native clinical record integrated with the calendar",
        "Automatic recall, smart waitlist, native WhatsApp",
        "AI for medical history, intake, prescriptions, clinical suggestions",
        "GDPR · consent · digital signatures by design",
        "Unified data layer · real-time analytics",
      ],
    },
  };
  const t = COPY[language] || COPY.it;

  return (
    <section
      id="platform"
      className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-white dark:bg-[#070B14]"
      data-testid="comparative-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-14">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 dark:text-white">
            {t.title}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 dark:text-slate-400">{t.sub}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Legacy column */}
          <div className="rounded-3xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-900/40 p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-7">
              <div className="h-10 w-10 rounded-2xl bg-slate-200 dark:bg-slate-800 flex items-center justify-center">
                <span className="text-slate-500 text-sm">×</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300">
                {t.legacy}
              </h3>
            </div>
            <ul className="space-y-4">
              {t.legacyItems.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-500 dark:text-slate-400 line-through decoration-slate-400/40">
                  <span className="mt-1.5 h-1 w-1 rounded-full bg-slate-400" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Modern column */}
          <div
            className="relative rounded-3xl border border-slate-900/10 dark:border-cyan-400/20 p-8 sm:p-10 overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, rgba(56,189,248,0.06), rgba(99,102,241,0.06) 60%, rgba(236,72,153,0.05))",
            }}
          >
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl bg-cyan-400/20 dark:bg-cyan-400/15" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-7">
                <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-cyan-400 to-indigo-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{t.modern}</h3>
              </div>
              <ul className="space-y-4">
                {t.modernItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-800 dark:text-slate-100">
                    <span className="mt-1 inline-flex h-5 w-5 rounded-full bg-gradient-to-br from-cyan-400 to-indigo-500 items-center justify-center flex-shrink-0">
                      <svg viewBox="0 0 24 24" fill="none" className="h-3 w-3 text-white">
                        <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   TESTIMONIALS — one per sector, elegant & credible
============================================================ */
const TESTIMONIALS = [
  {
    sectorKey: "dentisti",
    sectorLabel_it: "Studi Dentistici",
    sectorLabel_en: "Dental Practices",
    accent: "#3B82F6",
    initials: "DR",
    author: "Dr.ssa Daniela Russo",
    role_it: "Direttore Sanitario · Itinera Medica · Bari",
    role_en: "Medical Director · Itinera Medica · Bari",
    quote_it:
      "In 3 mesi abbiamo digitalizzato anamnesi, consensi e ricette SSN. Ricevere un'analisi AI dei pazienti prima della visita ha cambiato il nostro approccio clinico — e liberato la segreteria dal telefono.",
    quote_en:
      "In 3 months we digitalized medical history, consent forms and SSN prescriptions. Receiving an AI analysis of patients before the visit changed our clinical approach — and freed our front desk from the phone.",
    metric_it: "−65% tempo segreteria",
    metric_en: "−65% front-desk time",
  },
  {
    sectorKey: "wellness",
    sectorLabel_it: "Benessere & Postura",
    sectorLabel_en: "Wellness & Posture",
    accent: "#10B981",
    initials: "AC",
    author: "Andrea Calvi",
    role_it: "Fisioterapista · Studio Posturale · Milano",
    role_en: "Physiotherapist · Studio Posturale · Milan",
    quote_it:
      "Cartelle cliniche, routine esercizi personalizzate e firme digitali in un unico flusso. I miei pazienti ricevono il protocollo via WhatsApp prima di uscire dallo studio: aderenza alle terapie aumentata in modo netto.",
    quote_en:
      "Clinical records, personalized exercise routines and digital signatures in a single flow. My patients receive the protocol via WhatsApp before leaving the studio: therapy adherence has clearly increased.",
    metric_it: "+42% aderenza terapia",
    metric_en: "+42% therapy adherence",
  },
  {
    sectorKey: "hair",
    sectorLabel_it: "Hair & Barber",
    sectorLabel_en: "Hair & Barber",
    accent: "#F59E0B",
    initials: "MR",
    author: "Matteo Riva",
    role_it: "Owner · House of Riva Barber · Torino",
    role_en: "Owner · House of Riva Barber · Turin",
    quote_it:
      "Booking online, recall automatici e una dashboard che mi dice ogni mattina chi sta per smettere di tornare. Ho smesso di rincorrere clienti — adesso il sistema lo fa per me, 24/7.",
    quote_en:
      "Online booking, automatic recall and a dashboard that tells me every morning who is about to stop coming back. I stopped chasing clients — now the system does it for me, 24/7.",
    metric_it: "+28% clienti ricorrenti",
    metric_en: "+28% recurring clients",
  },
  {
    sectorKey: "beauty",
    sectorLabel_it: "Estetica & Beauty",
    sectorLabel_en: "Aesthetics & Beauty",
    accent: "#EC4899",
    initials: "GS",
    author: "Giulia Santarelli",
    role_it: "Founder · Lumiere Beauty Lab · Roma",
    role_en: "Founder · Lumiere Beauty Lab · Rome",
    quote_it:
      "Gestiamo tre cabine, due estetiste e un percorso trattamento da otto sedute con un solo strumento. Le clienti firmano i consensi sul tablet e ricevono il piano via email. Sembra di lavorare in una clinica del 2030.",
    quote_en:
      "We manage three cabins, two beauticians and an eight-session treatment journey with a single tool. Clients sign consents on the tablet and receive the plan via email. It feels like working in a 2030 clinic.",
    metric_it: "3 cabine · 1 sistema",
    metric_en: "3 cabins · 1 system",
  },
];

const TestimonialCard = ({ t, language }) => {
  const role = language === "en" ? t.role_en : t.role_it;
  const quote = language === "en" ? t.quote_en : t.quote_it;
  const metric = language === "en" ? t.metric_en : t.metric_it;

  return (
    <article
      className="group relative flex flex-col rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/40 backdrop-blur-sm overflow-hidden p-6 sm:p-7 transition-all duration-500 hover:-translate-y-1 hover:border-slate-300 dark:hover:border-white/20 hover:shadow-[0_30px_80px_-30px_rgba(15,23,42,0.25)] dark:hover:shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
      data-testid={`testimonial-${t.sectorKey}`}
    >
      {/* Subtle accent line on top */}
      <div
        className="absolute top-0 left-0 h-[3px] w-16 transition-all duration-500 group-hover:w-full opacity-80"
        style={{ background: `linear-gradient(90deg, ${t.accent}, transparent)` }}
      />

      {/* Header: verified badge only (sector eyebrow removed for cleaner look) */}
      <div className="flex items-center justify-end mb-6">
        <div
          className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400"
          title={language === "it" ? "Cliente verificato" : "Verified customer"}
        >
          <BadgeCheck className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400" />
          {language === "it" ? "Verificato" : "Verified"}
        </div>
      </div>

      {/* Decorative quote mark */}
      <Quote
        className="absolute opacity-[0.06] dark:opacity-[0.08] w-24 h-24 -top-1 -right-1"
        style={{ color: t.accent }}
      />

      {/* Stars */}
      <div className="flex items-center gap-0.5 mb-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <Star
            key={i}
            className="w-3.5 h-3.5 fill-amber-400 text-amber-400"
          />
        ))}
      </div>

      {/* Quote */}
      <p className="relative text-sm font-medium text-slate-800 dark:text-slate-100 leading-relaxed flex-1">
        <span className="opacity-50 mr-0.5">{`"`}</span>
        {quote}
        <span className="opacity-50 ml-0.5">{`"`}</span>
      </p>

      {/* Author */}
      <div className="mt-6 flex items-center gap-3">
        <div
          className="h-11 w-11 rounded-full flex items-center justify-center text-xs font-semibold text-white shadow-lg flex-shrink-0"
          style={{
            background: `linear-gradient(135deg, ${t.accent}, ${t.accent}aa)`,
            boxShadow: `0 8px 24px -8px ${t.accent}66`,
          }}
        >
          {t.initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-slate-900 dark:text-white truncate">
            {t.author}
          </p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            {role}
          </p>
        </div>
      </div>

      {/* Metric chip — full-width below for symmetry across all cards */}
      {metric && (
        <div
          className="mt-4 inline-flex items-center justify-center px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide self-start"
          style={{
            background: `${t.accent}12`,
            color: t.accent,
            border: `1px solid ${t.accent}26`,
          }}
        >
          {metric}
        </div>
      )}
    </article>
  );
};

const Testimonials = ({ language }) => {
  return (
    <section
      id="testimonials"
      className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-[#050810] overflow-hidden"
      data-testid="testimonials-section"
    >
      {/* Background grain / aurora */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-20 left-1/4 w-[500px] h-[500px] rounded-full blur-[140px] bg-cyan-400/10 dark:bg-cyan-400/10" />
        <div className="absolute bottom-20 right-1/4 w-[500px] h-[500px] rounded-full blur-[140px] bg-rose-400/10 dark:bg-rose-400/10" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 dark:text-white"
              data-testid="testimonials-title"
            >
              {language === "it" ? (
                <>
                  Chi usa SmartBooker.
                  <br />
                  <span className="text-slate-500 dark:text-slate-400">
                    Quattro storie, quattro mondi.
                  </span>
                </>
              ) : (
                <>
                  Who runs on SmartBooker.
                  <br />
                  <span className="text-slate-500 dark:text-slate-400">
                    Four stories, four worlds.
                  </span>
                </>
              )}
            </h2>
          </div>
          <div className="max-w-md text-base text-slate-600 dark:text-slate-400">
            <p>
              {language === "it"
                ? "Storie reali da professionisti che hanno scelto un sistema progettato sul loro mestiere — non un gestionale generico riadattato."
                : "Real stories from professionals who chose a system designed around their craft — not a generic tool retrofitted to it."}
            </p>
            <div className="mt-5 inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              <span className="inline-flex items-center gap-1.5">
                <BadgeCheck className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400" />
                {language === "it" ? "Identità verificate" : "Verified identities"}
              </span>
              <span className="w-px h-3 bg-slate-300 dark:bg-white/15" />
              <span>
                {language === "it" ? "Su consenso scritto" : "With written consent"}
              </span>
            </div>
          </div>
        </div>

        {/* 4 testimonianze affiancate per ottimizzare gli spazi */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="testimonials-grid">
          {TESTIMONIALS.map((tt) => (
            <TestimonialCard key={tt.sectorKey} t={tt} language={language} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   BENEFITS — pillars of value
============================================================ */
const Benefits = ({ language }) => {
  const COPY = {
    it: {
      eyebrow: "Pillars",
      title: "Le fondamenta della piattaforma",
      sub: "Costruita su quattro principi non negoziabili che attraversano ogni verticale.",
      items: [
        {
          icon: ShieldCheck,
          title: "Compliance by design",
          desc: "GDPR, consensi, firme digitali e archiviazione documentale integrati nel core della piattaforma.",
        },
        {
          icon: Brain,
          title: "AI verticale",
          desc: "Modelli specializzati per anamnesi cliniche, intake automatici, ricette e analisi del paziente.",
        },
        {
          icon: Cpu,
          title: "Operations automatizzate",
          desc: "Recall, promemoria, lista d'attesa intelligente e workflow operativi che lavorano per te.",
        },
        {
          icon: LineChart,
          title: "Analytics in tempo reale",
          desc: "KPI economici, clinici e operativi sempre visibili. Decisioni basate sui dati, non sulle sensazioni.",
        },
      ],
    },
    en: {
      eyebrow: "Pillars",
      title: "The foundations of the platform",
      sub: "Built on four non-negotiable principles that run through every vertical.",
      items: [
        {
          icon: ShieldCheck,
          title: "Compliance by design",
          desc: "GDPR, consents, digital signatures and document archiving integrated into the platform core.",
        },
        {
          icon: Brain,
          title: "Vertical AI",
          desc: "Specialized models for clinical history, automatic intake, prescriptions and patient analytics.",
        },
        {
          icon: Cpu,
          title: "Automated operations",
          desc: "Recall, reminders, smart waitlist and operational workflows working for you.",
        },
        {
          icon: LineChart,
          title: "Real-time analytics",
          desc: "Economic, clinical and operational KPIs always visible. Data-driven decisions, not guesses.",
        },
      ],
    },
  };
  const t = COPY[language] || COPY.it;

  return (
    <section
      className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-[#050810]"
      data-testid="benefits-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-14">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 dark:text-white">
            {t.title}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 dark:text-slate-400">{t.sub}</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.items.map((item, i) => {
            const Icon = item.icon;
            return (
              <div
                key={i}
                className="group relative rounded-3xl bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-white/10 p-7 hover:border-slate-300 dark:hover:border-cyan-400/30 hover:-translate-y-1 transition-all duration-500"
                data-testid={`benefit-${i}`}
              >
                <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 dark:from-cyan-400 dark:to-indigo-500 flex items-center justify-center shadow-lg shadow-slate-900/10 dark:shadow-cyan-500/20 mb-6 transition-transform group-hover:rotate-[-4deg] group-hover:scale-110">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   TECH SECTION — benefit-focused (no jargon, target = non-tech pros)
============================================================ */
const TechSection = ({ language }) => {
  const COPY = {
    it: {
      title: "Una piattaforma costruita per farti lavorare meglio.",
      sub: "Mentre tu ti occupi dei tuoi clienti e pazienti, SmartBooker lavora dietro le quinte per organizzare, automatizzare e semplificare la gestione della tua attività.",
      trustLine: ["Semplice da usare", "Sempre sicuro", "Pronto quando ti serve", "Fatto per crescere"],
      pillars: [
        {
          icon: ShieldCheck,
          label: "Massima sicurezza",
          desc: "I tuoi dati e quelli dei tuoi clienti sono sempre protetti grazie a sistemi di sicurezza avanzati e backup automatici.",
        },
        {
          icon: Smartphone,
          label: "Ovunque tu sia",
          desc: "Accedi a SmartBooker da PC, tablet o smartphone e gestisci la tua attività in qualsiasi momento.",
        },
        {
          icon: Zap,
          label: "Più tempo per il tuo lavoro",
          desc: "Automatizza le attività ripetitive e riduci il tempo dedicato alla gestione quotidiana.",
        },
        {
          icon: Rocket,
          label: "Pronto per il futuro",
          desc: "Una piattaforma moderna che cresce insieme alla tua attività e si adatta alle tue esigenze.",
        },
      ],
    },
    en: {
      title: "A platform built to help you work better.",
      sub: "While you take care of your clients and patients, SmartBooker works behind the scenes to organize, automate and simplify the management of your business.",
      trustLine: ["Easy to use", "Always secure", "Ready when you need it", "Built to grow"],
      pillars: [
        {
          icon: ShieldCheck,
          label: "Maximum security",
          desc: "Your data and your clients' data are always protected with advanced security systems and automatic backups.",
        },
        {
          icon: Smartphone,
          label: "Wherever you are",
          desc: "Access SmartBooker from PC, tablet or smartphone and manage your business at any time.",
        },
        {
          icon: Zap,
          label: "More time for your work",
          desc: "Automate repetitive tasks and reduce the time spent on day-to-day management.",
        },
        {
          icon: Rocket,
          label: "Ready for the future",
          desc: "A modern platform that grows with your business and adapts to your needs.",
        },
      ],
    },
  };
  const t = COPY[language] || COPY.it;

  return (
    <section
      id="tech"
      className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden bg-[#070B14] text-white"
      data-testid="tech-section"
    >
      {/* aurora */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-20 w-[600px] h-[600px] rounded-full blur-[140px] bg-cyan-500/20" />
        <div className="absolute -bottom-40 -right-20 w-[600px] h-[600px] rounded-full blur-[140px] bg-indigo-500/20" />
      </div>
      <div className="relative max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              {t.title}
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-300">{t.sub}</p>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-[11px] uppercase tracking-[0.22em] text-slate-400">
              {t.trustLine.map((item, idx) => (
                <React.Fragment key={item}>
                  {idx > 0 && <span className="w-px h-3 bg-white/15" />}
                  <span>{item}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {t.pillars.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.label}
                  className="group relative rounded-2xl bg-white/[0.03] border border-white/10 p-6 hover:border-cyan-400/30 hover:bg-white/[0.06] transition-all"
                  data-testid={`tech-pillar-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-cyan-400/20 to-indigo-500/20 border border-cyan-400/20 flex items-center justify-center mb-5 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-[-4deg]">
                    <Icon className="w-5 h-5 text-cyan-300" />
                  </div>
                  <h4 className="text-base font-semibold tracking-tight">{p.label}</h4>
                  <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                  <div className="absolute inset-x-6 bottom-3 h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   DEMO SECTION — institutional CTA (no pricing, no trial)
============================================================ */
const DemoSection = ({ language }) => {
  return (
    <section
      id="demo"
      className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-white dark:bg-[#070B14]"
      data-testid="demo-section"
    >
      <div className="max-w-5xl mx-auto">
        <div className="relative rounded-[2.5rem] overflow-hidden border border-slate-200 dark:border-white/10 bg-gradient-to-br from-slate-900 via-slate-900 to-[#0a1226] text-white p-10 sm:p-16">
          <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full blur-[120px] bg-cyan-400/30" />
          <div className="absolute -bottom-32 -left-24 w-[400px] h-[400px] rounded-full blur-[120px] bg-indigo-500/30" />
          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
                {language === "it"
                  ? "Richiedi una demo personalizzata."
                  : "Request a personalized demo."}
              </h2>
              <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-xl">
                {language === "it"
                  ? "Un consulente del tuo settore ti mostrerà la piattaforma con dati reali, casi d'uso e configurazione su misura per la tua attività."
                  : "A consultant from your sector will walk you through the platform with real data, use cases and a configuration tailored to your business."}
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link to="/demo">
                  <Button
                    size="lg"
                    className="rounded-full bg-white text-slate-900 hover:bg-slate-100 px-7 py-6 text-sm font-semibold tracking-wide"
                    data-testid="demo-cta-primary"
                  >
                    {language === "it" ? "Richiedi una demo" : "Request a demo"}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a
                  href="#sectors"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-white/20 text-white text-sm font-medium hover:bg-white/5 transition-colors"
                  data-testid="demo-cta-secondary"
                >
                  {language === "it" ? "Esplora i settori" : "Explore sectors"}
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-3">
                {SECTORS.map((s) => (
                  <div
                    key={s.key}
                    className="aspect-square rounded-2xl overflow-hidden border border-white/10"
                    style={{ boxShadow: `0 0 0 1px ${s.accent}10` }}
                  >
                    <img src={s.image} alt={s[language]?.title || s.it.title} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   FOOTER — minimal corporate
============================================================ */
const Footer = ({ language }) => {
  return (
    <footer
      className="py-16 px-4 sm:px-6 lg:px-8 border-t border-slate-200 dark:border-white/5 bg-white dark:bg-[#050810]"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-6 gap-10 mb-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <img src="/brand/sb-icon.png" alt="SmartBooker" className="h-9 w-9" />
              <span className="font-semibold text-lg text-slate-900 dark:text-white">
                SmartBooker
              </span>
            </div>
            <p className="text-slate-500 dark:text-slate-400 max-w-md text-sm leading-relaxed">
              {language === "it"
                ? "La piattaforma SaaS verticale per studi dentistici, centri di benessere, hair salon e centri estetici."
                : "The vertical SaaS platform for dental clinics, wellness centers, hair salons and beauty centers."}
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-slate-900 dark:text-white mb-4 text-sm">
              {language === "it" ? "Settori" : "Sectors"}
            </h4>
            <div className="space-y-3">
              {SECTORS.map((s) => (
                <Link
                  key={s.key}
                  to={s.route}
                  className="block text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
                  data-testid={`footer-sector-${s.key}`}
                >
                  {s[language]?.title || s.it.title}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-slate-900 dark:text-white mb-4 text-sm">
              {language === "it" ? "Piattaforma" : "Platform"}
            </h4>
            <div className="space-y-3">
              <a href="#platform" className="block text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                {language === "it" ? "Come funziona" : "How it works"}
              </a>
              <a href="#tech" className="block text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                {language === "it" ? "Perché SmartBooker" : "Why SmartBooker"}
              </a>
              <Link to="/demo" className="block text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                Demo
              </Link>
              <Link to="/login" className="block text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                {language === "it" ? "Accedi" : "Sign in"}
              </Link>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-slate-900 dark:text-white mb-4 text-sm">
              {language === "it" ? "Legale" : "Legal"}
            </h4>
            <div className="space-y-3">
              <Link to="/privacy-policy" className="block text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/cookie-policy" className="block text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                Cookie Policy
              </Link>
              <Link to="/terms-conditions" className="block text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                {language === "it" ? "Termini" : "Terms"}
              </Link>
              <Link to="/dpa" className="block text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                DPA
              </Link>
              <Link to="/trust-security" className="block text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                Trust & Security
              </Link>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-slate-900 dark:text-white mb-4 text-sm">
              {language === "it" ? "Contatti" : "Contact"}
            </h4>
            <div className="space-y-4">
              <a
                href="https://wa.me/393770873260"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-slate-500 dark:text-slate-400 hover:text-emerald-500 transition-colors group"
                data-testid="footer-whatsapp"
              >
                <div className="w-9 h-9 rounded-full bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                  <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </div>
                <div>
                  <span className="block text-sm font-medium">WhatsApp</span>
                  <span className="block text-xs">+39 377 087 3260</span>
                </div>
              </a>
              <a
                href="mailto:info@smartbooker.it"
                className="flex items-center gap-3 text-slate-500 dark:text-slate-400 hover:text-cyan-500 transition-colors group"
                data-testid="footer-email"
              >
                <div className="w-9 h-9 rounded-full bg-cyan-500/10 flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
                  <Mail className="w-4 h-4 text-cyan-500" />
                </div>
                <div>
                  <span className="block text-sm font-medium">Email</span>
                  <span className="block text-xs">info@smartbooker.it</span>
                </div>
              </a>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-200 dark:border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500 dark:text-slate-500">
            © 2026 SmartBooker — Powered by AleNic Service and Consulting
          </p>
          <button
            onClick={() => window.openCookiePreferences && window.openCookiePreferences()}
            className="text-sm text-slate-500 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors"
            data-testid="manage-cookies-btn"
          >
            {language === "it" ? "Gestisci Cookie" : "Manage Cookies"}
          </button>
        </div>
      </div>
    </footer>
  );
};

/* ============================================================
   ROOT LANDING PAGE
============================================================ */
const Landing = () => {
  const { language, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const trackedRef = useRef(false);

  // Track landing_view once per page mount (Super Admin Dashboard counter)
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    axios
      .post(`${API}/analytics/track`, {
        event: "landing_view",
        referrer: document.referrer || "",
      })
      .catch(() => {});
  }, []);

  // Anchor smooth scroll for in-page links
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-white dark:bg-[#070B14] text-slate-900 dark:text-slate-100 antialiased"
      data-testid="landing-page"
    >
      <TopNav
        language={language}
        changeLanguage={changeLanguage}
        theme={theme}
        toggleTheme={toggleTheme}
      />
      <main>
        <Hero language={language} />
        <SectorHub language={language} />
        <Comparative language={language} />
        <Testimonials language={language} />
        <Benefits language={language} />
        <TechSection language={language} />
        <DemoSection language={language} />
      </main>
      <Footer language={language} />
    </div>
  );
};

export default Landing;
